import { stringify, stringifyUrl } from "query-string";

import {
    GET_LIST,
    GET_ONE,
    CREATE,
    UPDATE,
    UPDATE_MANY,
    DELETE,
    DELETE_MANY,
    GET_MANY,
    GET_MANY_REFERENCE,
    HttpError,
} from "react-admin";

import { config } from "../config";
import { userService } from "../services/UserService";
import { telemetryService } from "../services/TelemetryService";

/**
 * Maps react-admin queries to the REST API
 *
 * IMPORTANT: React-admin v5 Breaking Changes
 * - UPDATE operations: The id field is no longer included in params.data
 *   It's now only available as params.id. This data provider adds it back
 *   to the request body for backend compatibility.
 *
 * @param {string} type Request type, e.g GET_LIST
 * @param {string} resource Resource name, e.g. "posts"
 * @param {Object} params Request parameters. Depends on the request type
 * @returns {Promise} the Promise for a data response
 */
export default (type, resourceStr, params) => {
    return new Promise(async (resolve, reject) => {
        const { apiName, resource, resourceType, apiUrl } = config.getResourceUrl(resourceStr);

        let url = "";
        let operation = null;

        let httpHeaders = {
            "Content-Type": "application/json",
            Authorization: `${userService.getAuthToken()}`,
            "x-client-type": "portal",
        };

        const options = {
            headers: httpHeaders,
        };

        if (apiName === "telemetry") {
            switch (type) {
                case GET_LIST:
                    return resolve(await telemetryApiProvider.list({ resourceType, resource, params }));

                case GET_ONE:
                    return resolve(await telemetryApiProvider.getOne({ id: params.id, resource }));

                case UPDATE:
                    return resolve(await telemetryApiProvider.update({ params, resource }));

                case CREATE:
                    return resolve(await telemetryApiProvider.create({ params, resource }));

                case DELETE:
                    return resolve(await telemetryApiProvider.delete({ id: params.id, resource }));
            }
        }

        switch (type) {
            case GET_LIST: {
                const { page, perPage } = params.pagination;
                let { field, order } = params.sort;

                let arr = resource.split("/");

                if ((arr[0] == "conductorSpans" || arr[0] == "conductorSpanBundles") && field == "id") {
                    field = "conductorId";
                } else if (arr[0] == "emfLineSourceSiteGroups" && field == "id") {
                    field = "emfSourceSiteGroupId";
                } else if (arr[0] == "monitorSiteSiteGroups" && field == "id") {
                    field = "monitorSiteId";
                }

                let query = {
                    sort: JSON.stringify([field, order]),
                    range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
                    filter: JSON.stringify(params.filter),
                };

                if (apiName !== "masterdata") {
                    operation = (data) => {
                        let sortOrder = order === "DESC" ? "-" : "";

                        // Enhancement to only sort if available
                        //TODO change all endpoins from web-api to send total items
                        if (data?.rows) {
                            data.rows.sort(dynamicSort(sortOrder + field));
                        } else {
                            data.sort(dynamicSort(sortOrder + field));
                        }

                        // Do client-side filtering only for the sites summary page (for now)
                        if (arr[0] === "sitesSummary") {
                            const f = params.filter;
                            data = data.filter((r) => {
                                if (
                                    (!f.id || r.id == f.id) &&
                                    (!f.model_type || f.model_type.includes(r.model_type)) &&
                                    (!f.client_id || r.client_id == f.client_id) &&
                                    (!f.lidar_ed_id || r.lidar_ed_id == f.lidar_ed_id) &&
                                    (!f.online_date || r.online_date >= f.online_date) &&
                                    (!f.short_name || r.short_name.toLowerCase().includes(f.short_name.toLowerCase()))
                                ) {
                                    return r;
                                }
                            });
                        }

                        return data;
                    };
                }

                url = `${apiUrl}/${resource}`;

                // TODO: Fix the hard-coded resource names here. this currently requires you to know of this location and
                // add to it everytime you need a resource that is able to filer.
                if (
                    resource === "users" ||
                    resource === "lineRoleGroups" ||
                    resource === "auditTrail" ||
                    resource === "sites" ||
                    resource === "onPremFiles" ||
                    apiName !== "portal"
                ) {
                    // sites resource takes in individual filter params
                    if (resource === "sites") {
                        query.lineIdsFilter = params.filter[0] || "";
                        query.lineNameFilter = params.filter[1] || "";
                        query.siteGroupNameFilter = params.filter[2] || "";
                    }

                    url = `${apiUrl}/${resource}?${stringify(query)}`;
                }

                if (apiName == "masterdata") {
                    url = stringifyUrl({
                        url: `${apiUrl}/${resource}`,
                        query: {
                            sortField: field,
                            sortOrder: order,
                            pageNumber: page,
                            perPage: perPage,
                            filter: JSON.stringify(params.filter),
                        },
                    });
                }

                break;
            }
            case GET_ONE:
                url = `${apiUrl}/${resource}/${params.id}`;
                break;
            case CREATE:
                url = `${apiUrl}/${resource}`;
                options.method = "POST";
                options.body = JSON.stringify(params.data);
                break;
            case UPDATE: {
                const recordId = params.id || params.data?.id;
                url = `${apiUrl}/${resource}/${recordId}`;
                options.method = "PUT";

                // React-admin v5 breaking change: id field is no longer included in params.data
                // We must add it back for backend API compatibility
                let updateData = {
                    ...params.data,
                    id: recordId,
                };

                options.body = JSON.stringify(updateData);
                break;
            }
            case UPDATE_MANY: {
                let query = {
                    filter: JSON.stringify({ id: params.ids }),
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                options.method = "PATCH";
                options.body = JSON.stringify(params.data);
                break;
            }
            case DELETE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = "DELETE";
                break;
            case DELETE_MANY: {
                const query = {
                    filter: JSON.stringify({ id: params.ids }),
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                options.method = "DELETE";
                break;
            }
            case GET_MANY: {
                const query = {
                    filter: JSON.stringify({ id: params.ids }),
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_MANY_REFERENCE: {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;

                let query = {
                    sort: JSON.stringify([field, order]),
                    range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
                    filter: JSON.stringify({
                        ...params.filter,
                        [params.target]: params.id,
                    }),
                };

                operation = (data) => {
                    let sortOrder = order === "DESC" ? "-" : "";

                    data.sort(dynamicSort(sortOrder + field));
                    return data;
                };

                url = `${apiUrl}/${resource}?${stringify(query)}`;

                break;
            }
            default:
                throw new Error(`Unsupported Data Provider request type ${type}`);
        }

        const res = await fetch(url, options);

        if (!res.ok) {
            let errorMessage = res.statusText;
            try {
                const errorBody = await res.json();
                if (errorBody && errorBody.message) {
                    errorMessage = errorBody.message;
                }
            } catch (e) {
                // response is not JSON, keep statusText
            }
            return reject(new HttpError(errorMessage, res.status));
        }

        let responseJson;

        if (res.status === 204) {
            responseJson = { data: {} };
        } else {
            responseJson = await res.json();
        }

        if (!responseJson || (Object.prototype.hasOwnProperty.call(responseJson, "success") && !responseJson.success)) {
            reject(new HttpError((responseJson && responseJson.message) || "An unknown error has occurred"), 500);
        } else {
            // Once we standardize the API response for all MD entities
            // we can remove this
            if ((type == "GET_LIST" || type == "GET_MANY") && apiName == "masterdata") {
                resolve(responseJson); // Masterdata returns the total / data as needed
            } else if (resource === "sitesSummary") {
                const data = operation ? operation(responseJson) : responseJson;

                const { page, perPage } = params.pagination;

                const startIndex = (page - 1) * perPage;
                const endIndex = page * perPage;

                const slicedData = data.slice(startIndex, endIndex);

                resolve({
                    total: data.length,
                    data: slicedData,
                });
            } else if (type === UPDATE && resource === "sites" && apiName === "portal") {
                // special handling for updating sites to set id from siteGroupId
                // (react admin complains otherwise that response shape has no 'id' field)
                if (responseJson && responseJson.siteGroupId) {
                    responseJson.id = responseJson.siteGroupId;
                }
                resolve({ data: responseJson });
            } else {
                const data = operation ? operation(responseJson) : responseJson;
                //TODO change all endpoins from web-api to send total items
                resolve({
                    total: responseJson?.count || data.length,
                    data: responseJson?.rows || data,
                });
            }
        }
    });
};

function dynamicSort(property) {
    let sortOrder = 1;

    if (property[0] === "-") {
        sortOrder = -1;
        property = property.substr(1);
    }

    return function (a, b) {
        const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
        return result * sortOrder;
    };
}

const telemetryApiProvider = {
    async list({ resourceType, resource, params }) {
        let httpResponse;
        let entries;
        switch (resource) {
            case "edgeConfig":
                httpResponse = await telemetryService.getAllEdgeConfigs(resourceType);
                entries = httpResponse.data;
                break;
            case "burnConfig":
                httpResponse = await telemetryService.getAllBurnConfigs(params);
                if (!httpResponse) {
                    return {
                        data: [],
                        total: 0,
                    };
                }
                entries = httpResponse.data.entries;
                break;
            default:
                console.warn("Unrecognized resource: " + resource);
        }

        const data = entries
            .map((item) => ({
                id: resource == "burnConfig" ? item.edge_device_id + "/" + item.version_no : item.serial_no,
                ...item,
            }))
            .filter((item) => {
                const isGeneric = item.id.startsWith("generic");

                if (isGeneric) {
                    console.warn(`Generic edge config with id "${item.id}" excluded from result`);
                }

                return !isGeneric;
            });

        return {
            data,
            total: resource == "burnConfig" ? httpResponse.data.num_entries : data.length,
        };
    },

    async getOne({ id, resource }) {
        let httpResponse;
        switch (resource) {
            case "edgeConfig":
                httpResponse = await telemetryService.getEdgeConfig(id);
                break;
            case "burnConfig":
                httpResponse = await telemetryService.getBurnConfig(id);
                break;
            default:
                console.warn("Unrecognized resource: " + resource);
        }
        const data = httpResponse.data;
        const result = {
            data: {
                id: resource == "burnConfig" ? data.edge_device_id + "/" + data.version_no : data.serial_no,
                ...data,
            },
        };

        return result;
    },

    async update({ params, resource }) {
        if (resource == "burnConfig") {
            const newData = params.data;
            const httpResponse = await telemetryService.saveBurnConfig(newData.edge_device_id, newData);
            const data = httpResponse.data[0];

            return { data: { id: data.edge_device_id + "/" + data.version_no, ...data } };
        }

        const httpResponse = await telemetryService.saveEdgeConfig(params.id, params.data.device_config);
        const data = httpResponse.data;

        return { data: { ...data, id: data.serial_no } };
    },

    async create({ params, resource }) {
        if (resource == "burnConfig") {
            const data = params.data;
            const newData = {
                id: data.edge_device_id,
                edge_device_id: data.edge_device_id,
                version_no: 0,
                serial_no: data.serial_no,
                inserted_dttm: null,
                inserted_by: data.inserted_by,
                comment: data.comment,
                burn_configs: {
                    serial: data.serial_no,
                    burn_factor: data.burn_factor,
                    edge_device_id: data.edge_device_id,
                    burn_conditions: {
                        force_burn: data.force_burn ? true : false,
                        below_cloud_cover: data.below_cloud_cover,
                        below_point_count: data.below_point_count,
                        above_battery_voltage: data.above_battery_voltage,
                        below_excess_temperature: data.below_excess_temperature,
                        below_dewpoint_depression: data.below_dewpoint_depression,
                    },
                    preheat_interval: data.preheat_interval,
                },
            };

            const httpResponse = await telemetryService.saveBurnConfig(newData.edge_device_id, newData);
            const result = httpResponse.data[0];

            return { data: { id: result.edge_device_id + "/" + result.version_no, ...result } };
        }

        // 'Create' functionality for edge configs not implemented yet
        return;
    },

    async delete({ id, resource }) {
        let httpResponse;

        if (resource == "burnConfig") {
            httpResponse = await telemetryService.deleteBurnConfig(id);
        } else {
            console.warn("Delete functionality only supported for burn configs");
            return;
        }

        const data = httpResponse.data;
        const result = {
            data: {
                id: resource == "burnConfig" ? data.edge_device_id + "/" + data.version_no : data.serial_no,
                ...data,
            },
        };

        return result;
    },
};
